/**
 * SEO Landing Page Data & Generators
 * Scalable system for generating high-converting SEO pages
 */

// ============ HELPERS ============

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function titleCase(str) {
  return str.split(" ").map(capitalize).join(" ");
}

const TRADE_META = {
  roofers:      { singular: "roofer",      plural: "roofers",      service: "roofing",      icon: "roof" },
  plumbers:     { singular: "plumber",     plural: "plumbers",     service: "plumbing",     icon: "pipe" },
  builders:     { singular: "builder",     plural: "builders",     service: "building",     icon: "brick" },
  electricians: { singular: "electrician", plural: "electricians", service: "electrical",   icon: "zap" },
  painters:     { singular: "painter",     plural: "painters",     service: "painting",     icon: "paint" },
  plasterers:   { singular: "plasterer",   plural: "plasterers",   service: "plastering",   icon: "wall" },
  landscapers:  { singular: "landscaper",  plural: "landscapers",  service: "landscaping",  icon: "tree" },
  carpenters:   { singular: "carpenter",   plural: "carpenters",   service: "carpentry",    icon: "hammer" },
  cleaners:     { singular: "cleaner",     plural: "cleaners",     service: "cleaning",     icon: "sparkle" },
};

const LOCATIONS = [
  "birmingham", "london", "manchester", "leeds", "liverpool",
  "bristol", "sheffield", "edinburgh", "glasgow", "cardiff",
  "nottingham", "newcastle", "brighton", "oxford", "cambridge",
];

// Launch configuration — which trade+location combos to generate
const LAUNCH_TRADES = ["roofers", "plumbers", "builders", "electricians", "painters", "plasterers", "landscapers", "carpenters"];
const LAUNCH_LOCATIONS = ["birmingham", "london", "manchester", "leeds", "liverpool", "bristol"];
const VERICLEAN_TRADES = ["cleaners"];
const VERICLEAN_LOCATIONS = ["birmingham", "london", "manchester", "leeds", "liverpool", "bristol"];

// ============ CONTENT VARIATION ============

const LOCATION_HOOKS = {
  birmingham: "As one of the UK's largest cities, Birmingham has no shortage of property projects on the go.",
  london: "With London's fast-moving property market, finding reliable trades quickly is essential.",
  manchester: "Manchester's mix of period terraces and new-builds means there's always work for skilled trades.",
  leeds: "Across Leeds, from the city centre to the suburbs, homeowners regularly need trusted tradespeople.",
  liverpool: "Liverpool's rich housing stock — from Georgian townhouses to modern flats — needs skilled care.",
  bristol: "Bristol's diverse property landscape, from Clifton to Bedminster, keeps local trades busy year-round.",
};

const TRADE_OPENERS = {
  roofers: "Your roof is your home's first line of defence against the British weather.",
  plumbers: "From dripping taps to full bathroom refits, good plumbing keeps your home running smoothly.",
  builders: "Whether you're extending, converting, or starting from scratch, a reliable builder is the key to any successful project.",
  electricians: "Safe, compliant electrical work isn't something to leave to chance.",
  painters: "A fresh coat of paint can completely transform a room — or an entire property.",
  plasterers: "Clean, smooth walls are the foundation of any good finish.",
  landscapers: "Your outdoor space deserves the same attention as the inside of your home.",
  carpenters: "From fitted wardrobes to structural timber work, skilled carpentry adds real value to a property.",
  cleaners: "A clean home isn't a luxury — it's a necessity.",
};

const TRADE_SPECIFICS = {
  roofers: "flat roof repairs, slate and tile replacement, chimney repointing, gutter work, and full re-roofs",
  plumbers: "bathroom installations, boiler servicing, radiator fitting, leak repairs, and pipe replacements",
  builders: "house extensions, loft conversions, garage conversions, renovations, and new builds",
  electricians: "rewiring, fuse board upgrades, socket installations, lighting design, and safety inspections",
  painters: "interior decoration, exterior painting, wallpaper hanging, wood staining, and spray finishes",
  plasterers: "skimming, rendering, dry lining, coving, and decorative plaster finishes",
  landscapers: "garden design, paving, fencing, decking, turfing, and drainage work",
  carpenters: "fitted kitchens, bespoke shelving, door hanging, flooring, and structural timber framing",
  cleaners: "regular house cleans, deep cleans, end of tenancy, oven cleaning, and commercial cleaning",
};

// ============ FAQ GENERATION ============

const ENTITY_PARAGRAPH = "VeriTrade is a UK platform that connects homeowners with verified tradespeople. Users can post a job and receive quotes without paying lead fees.";
const ENTITY_PARAGRAPH_CLEAN = "VeriClean is a UK platform that connects homeowners and businesses with verified cleaning professionals. Users can post a cleaning job and receive quotes without paying booking fees.";

function generateTradeLocationFaqs(trade, location) {
  const t = TRADE_META[trade];
  if (!t) return [];
  const loc = titleCase(location);
  const isClean = trade === "cleaners";
  const brand = isClean ? "VeriClean" : "VeriTrade";
  const ctaPath = isClean ? "/vericlean/book" : "/post-job";

  if (isClean) {
    return [
      { q: `How do I find a cleaner in ${loc}?`, a: `Post your cleaning job on VeriClean for free. Describe what you need, enter your ${loc} postcode, and verified cleaners nearby will send you quotes. You can also <a href="/vericlean/book">book a cleaner directly</a>.` },
      { q: `Are cleaners on VeriClean vetted?`, a: `Yes. Every cleaner on our platform goes through a verification process before they can respond to jobs. You can also check reviews from other customers in ${loc}.` },
      { q: `Can I get same-day cleaning in ${loc}?`, a: `It depends on availability, but many cleaners in ${loc} respond within hours. Mark your job as urgent for faster responses.` },
      { q: `Is it free to post a cleaning job?`, a: `Completely free. There are no booking fees, no lead charges, and no hidden costs. Post your job and compare quotes at no cost.` },
      { q: `What types of cleaning can I request?`, a: `Regular house cleans, deep cleans, end of tenancy, one-off cleans, oven cleaning, commercial cleaning, and more. Just describe what you need.` },
      { q: `Do I have to accept a quote?`, a: `No. There's no obligation to hire. You can compare multiple quotes from verified cleaners in ${loc} and choose the one that suits your budget and schedule.` },
    ];
  }

  return [
    { q: `How do I find a ${t.singular} in ${loc}?`, a: `Post your ${t.service} job on ${brand} — it's free. Describe the work, set your location to ${loc}, and verified ${t.plural} will send you quotes. <a href="${ctaPath}">Post your job here</a>.` },
    { q: `How quickly can I get ${t.service} quotes in ${loc}?`, a: `Most ${t.plural} in ${loc} respond within a few hours. For urgent work, you'll often get quotes on the same day you post.` },
    { q: `Is it free to post a ${t.service} job?`, a: `Yes, completely free. ${brand} never charges customers for posting jobs. There are no lead fees and no hidden costs.` },
    { q: `Are ${t.plural} on ${brand} verified?`, a: `Every ${t.singular} on our platform goes through a verification process. You can also view their profile, ratings, and reviews from previous customers.` },
    { q: `Do I have to accept a quote?`, a: `No. You're under no obligation. Compare quotes from multiple ${t.plural} in ${loc} and hire only when you're ready.` },
    { q: `What types of ${t.service} work can I post?`, a: `Anything from small repairs to large projects — ${TRADE_SPECIFICS[trade] || `all types of ${t.service} work`}. Just describe what you need and local ${t.plural} will respond.` },
  ];
}

function generateIntentFaqs(config) {
  return config.faqs || [];
}

// ============ GENERATORS ============

function generateTradeLocation(trade, location) {
  const t = TRADE_META[trade];
  if (!t) return null;
  const loc = titleCase(location);
  const isClean = trade === "cleaners";
  const ctaLink = isClean ? "/vericlean/book" : "/post-job";
  const brand = isClean ? "VeriClean" : "VeriTrade";
  const locHook = LOCATION_HOOKS[location] || "";
  const tradeOpener = TRADE_OPENERS[trade] || "";
  const tradeSpecifics = TRADE_SPECIFICS[trade] || "";

  return {
    slug: `${trade}-${location}`,
    type: "trade-location",
    isVeriClean: isClean,
    metaTitle: `Find ${capitalize(t.plural)} in ${loc} | ${brand} UK`,
    metaDescription: `Post your ${t.service} job and get quotes from verified ${t.plural} in ${loc}. No lead fees. Fast responses. Free to post.`,
    h1: `Find ${capitalize(t.plural)} in ${loc}`,
    subheadline: `Post your ${t.service} job for free and get quotes from trusted, verified ${t.plural} near you in ${loc}.`,
    ctaLink,
    ctaText: isClean ? "Book a Cleaner" : "Post Your Job",
    howItWorks: [
      { step: "1", title: isClean ? "Describe your clean" : "Post your job", desc: isClean ? "Tell us what you need cleaned and where." : `Tell us what ${t.service} work you need done.` },
      { step: "2", title: `Get quotes from ${t.plural}`, desc: `Verified ${t.plural} in ${loc} will respond with their availability and pricing.` },
      { step: "3", title: "Choose the best one", desc: `Compare profiles, reviews, and quotes. Pick the ${t.singular} that suits you.` },
    ],
    whyPoints: [
      "No lead fees — ever",
      `Verified ${t.plural} in ${loc}`,
      "Fast responses, usually same day",
      "Free to post your job",
      isClean ? "Trusted cleaners, background checked" : "UK-wide platform with local experts",
    ],
    trustText: `${tradeOpener} Finding a reliable ${t.singular} in ${loc} shouldn't be stressful. With ${brand}, you post your job once and let qualified, verified ${t.plural} come to you. There's no obligation to hire, and you'll receive multiple quotes so you can compare before deciding. Every ${t.singular} on our platform has been through our verification process.`,
    seoContent: `Looking for ${t.plural} in ${loc}? ${locHook} ${brand} connects homeowners and businesses in ${loc} with trusted, verified ${t.plural} ready to take on your ${t.service} project.\n\nOur ${loc}-based ${t.plural} cover ${tradeSpecifics}. Whether it's a small repair or a large-scale project, posting on ${brand} is completely free with no hidden fees or lead charges. Simply describe what you need, set your location to ${loc}, and receive quotes from qualified professionals in your area.\n\n${brand} is a UK-wide platform built to make hiring tradespeople straightforward and transparent. You can review profiles, check ratings, and choose the right ${t.singular} for your budget. No middleman fees, no hassle — just direct connections with real ${t.plural} in ${loc}.`,
    faqs: generateTradeLocationFaqs(trade, location),
    entityParagraph: isClean ? ENTITY_PARAGRAPH_CLEAN : ENTITY_PARAGRAPH,
    relatedLinks: generateRelatedLinks(trade, location, isClean),
  };
}

function generateIntentPage(config) {
  const isClean = config.isVeriClean || false;
  const ctaLink = isClean ? "/vericlean/book" : "/post-job";
  const brand = isClean ? "VeriClean" : "VeriTrade";

  return {
    slug: config.slug,
    type: "intent",
    isVeriClean: isClean,
    metaTitle: config.metaTitle,
    metaDescription: config.metaDescription,
    h1: config.h1,
    subheadline: config.subheadline,
    ctaLink,
    ctaText: config.ctaText || (isClean ? "Book a Cleaner" : "Post Your Job"),
    howItWorks: config.howItWorks || [
      { step: "1", title: "Post your job", desc: "Describe the work you need done and your location." },
      { step: "2", title: "Get quotes", desc: "Verified trades respond with pricing and availability." },
      { step: "3", title: "Choose and hire", desc: "Pick the best trade for your job. No obligation." },
    ],
    whyPoints: config.whyPoints || [
      "No lead fees — ever",
      "Verified tradespeople",
      "Fast responses, usually same day",
      "Free to post",
      "Compare multiple quotes",
    ],
    trustText: config.trustText,
    seoContent: config.seoContent,
    faqs: config.faqs || [],
    entityParagraph: isClean ? ENTITY_PARAGRAPH_CLEAN : ENTITY_PARAGRAPH,
    relatedLinks: config.relatedLinks || [],
  };
}

function generateRelatedLinks(trade, location, isClean) {
  const links = [];
  const base = isClean ? "/vericlean/book" : "/post-job";
  links.push({ label: isClean ? "Book a Cleaner" : "Post a Job", href: base });

  // Same trade in different locations (2 max)
  const otherLocations = LAUNCH_LOCATIONS.filter(l => l !== location).slice(0, 2);
  otherLocations.forEach(l => {
    const meta = TRADE_META[trade];
    links.push({
      label: `${capitalize(meta.plural)} in ${titleCase(l)}`,
      href: `/${trade}-${l}`,
    });
  });

  // Different trade in same location (2 max)
  const allTrades = isClean ? ["cleaners"] : Object.keys(TRADE_META).filter(t => t !== trade && t !== "cleaners");
  allTrades.slice(0, 2).forEach(t => {
    const meta = TRADE_META[t];
    if (t !== trade) {
      links.push({
        label: `${capitalize(meta.plural)} in ${titleCase(location)}`,
        href: `/${t}-${location}`,
      });
    }
  });

  // Relevant intent page
  const intentMap = {
    roofers: { label: "Get Roofing Quotes", href: "/get-roofing-quotes" },
    plumbers: { label: "Get Plumbing Quotes", href: "/get-plumbing-quotes" },
    builders: { label: "Post a Building Job", href: "/post-a-job-builder" },
    electricians: { label: "Find an Electrician", href: "/find-electrician-near-me" },
    painters: { label: "Hire a Painter", href: "/hire-a-painter" },
    cleaners: { label: "Book a Cleaner Near Me", href: "/book-a-cleaner-near-me" },
  };
  if (intentMap[trade]) links.push(intentMap[trade]);

  // Cross-platform link
  if (isClean) {
    links.push({ label: "Find Tradespeople", href: "/post-job" });
  } else {
    links.push({ label: "Need a Cleaner?", href: "/vericlean/book" });
  }

  // Competitor page (for trade pages only)
  if (!isClean) {
    links.push({ label: "Checkatrade Alternative", href: "/checkatrade-alternative" });
  }

  return links;
}

// ============ STATIC INTENT PAGES ============

const INTENT_PAGES = [
  generateIntentPage({
    slug: "get-roofing-quotes",
    metaTitle: "Get Roofing Quotes | Free, No Obligation | VeriTrade UK",
    metaDescription: "Post your roofing job and get free quotes from verified roofers near you. No lead fees. Compare and choose.",
    h1: "Get Roofing Quotes Near You",
    subheadline: "Describe your roofing job, sit back, and let qualified roofers send you quotes. Completely free, no obligation.",
    trustText: "Whether you need a roof repair, replacement, or a full new roof, getting quotes shouldn't cost you anything. On VeriTrade, you post once and receive multiple quotes from verified roofers. No lead fees, no pressure — just honest quotes from real tradespeople.",
    seoContent: "Need roofing work done? Getting quotes from reliable roofers is the first step to any successful roofing project. VeriTrade makes it simple — post the details of your job, including what needs doing and where you're based, and verified roofers in your area will respond with quotes.\n\nWhether it's a leaking roof, missing tiles, flat roof repair, or a complete re-roof, our platform connects you with experienced professionals. You can compare quotes, read reviews, and choose the roofer that fits your budget and timeline.\n\nThere are no lead fees on VeriTrade. Posting your job is free and there's no obligation to hire. It's the straightforward way to find roofing help in the UK.",
    relatedLinks: [
      { label: "Post a Job", href: "/post-job" },
      { label: "Roofers in Birmingham", href: "/roofers-birmingham" },
      { label: "Roofers in London", href: "/roofers-london" },
      { label: "Roofers in Manchester", href: "/roofers-manchester" },
    ],
    faqs: [
      { q: "How do I get roofing quotes?", a: 'Describe your roofing job on VeriTrade and verified roofers near you will respond with quotes. <a href="/post-job">Post your job here</a>.' },
      { q: "How many roofing quotes will I receive?", a: "It varies by location and job type, but most users receive between 2 and 5 quotes from local roofers." },
      { q: "How long does it take to get a roofing quote?", a: "Most roofers respond within a few hours. Urgent jobs often get same-day quotes." },
      { q: "Is there any obligation to hire?", a: "None at all. You can compare quotes and only proceed when you're ready." },
      { q: "Does it cost anything to get quotes?", a: "No. Posting your roofing job is completely free. There are no lead fees or hidden charges." },
    ],
  }),
  generateIntentPage({
    slug: "post-a-job-builder",
    metaTitle: "Post a Building Job | Find Builders Fast | VeriTrade UK",
    metaDescription: "Post your building job for free and get quotes from verified builders. Extensions, renovations, new builds — all covered.",
    h1: "Post a Job for a Builder",
    subheadline: "Extensions, renovations, loft conversions — whatever you need built, post it free and get quotes from verified builders.",
    trustText: "Finding a good builder can be tough. VeriTrade takes the hassle out of it. Post your building project, describe what you need, and let verified builders send you quotes. No cold calls, no lead fees, no stress.",
    seoContent: "Looking for a builder? Whether you're planning a house extension, loft conversion, renovation, or new build, posting your job on VeriTrade is the fastest way to find qualified builders in your area.\n\nSimply describe your project, add your location, and our verified builders will get in touch with quotes. You keep full control — review their profiles, check their work history, and choose when you're ready.\n\nVeriTrade never charges lead fees. Posting is free, and you're under no obligation to accept any quote. It's building work made simple.",
    relatedLinks: [
      { label: "Post a Job", href: "/post-job" },
      { label: "Builders in London", href: "/builders-london" },
      { label: "Builders in Manchester", href: "/builders-manchester" },
      { label: "Builders in Birmingham", href: "/builders-birmingham" },
    ],
    faqs: [
      { q: "How do I post a building job?", a: 'Go to <a href="/post-job">Post Your Job</a>, describe what you need built, add your location, and verified builders will respond with quotes.' },
      { q: "What types of building work can I post?", a: "Extensions, loft conversions, garage conversions, renovations, new builds, structural work — any building project." },
      { q: "Are builders on VeriTrade checked?", a: "Yes. All builders go through our verification process. You can also see their profile, ratings, and past work." },
      { q: "How quickly will builders respond?", a: "Most builders respond within hours. Response times vary by location and season." },
      { q: "Is it free to post a building job?", a: "Completely free. No lead fees, no subscription, no hidden costs." },
    ],
  }),
  generateIntentPage({
    slug: "emergency-plumber",
    metaTitle: "Emergency Plumber — Find One Fast | VeriTrade UK",
    metaDescription: "Need an emergency plumber? Post your job now and get fast responses from verified plumbers near you. Available 24/7.",
    h1: "Find an Emergency Plumber Fast",
    subheadline: "Burst pipe? Leak? Boiler failure? Post your emergency job now and get rapid responses from verified plumbers near you.",
    ctaText: "Post Emergency Job",
    howItWorks: [
      { step: "1", title: "Post your emergency", desc: "Describe the problem and mark it as urgent." },
      { step: "2", title: "Get fast responses", desc: "Verified plumbers near you respond quickly." },
      { step: "3", title: "Get it sorted", desc: "Choose a plumber and get your problem fixed." },
    ],
    whyPoints: [
      "Fast responses from local plumbers",
      "Verified and reviewed professionals",
      "No call-out booking fees",
      "Post your emergency for free",
      "Available across the UK",
    ],
    trustText: "When you've got a plumbing emergency, you need someone fast. VeriTrade connects you directly with verified plumbers in your area who can respond quickly. No agencies, no middlemen — just real plumbers ready to help.",
    seoContent: "Dealing with a plumbing emergency? A burst pipe, serious leak, or boiler breakdown needs immediate attention. VeriTrade helps you find verified emergency plumbers near you without the usual hassle of calling round agencies.\n\nPost your emergency job with a description of the issue and your postcode. Local plumbers who are available will get in touch directly. You can check their profile, see reviews from previous customers, and choose who you want to book.\n\nThere are no lead fees or booking charges on VeriTrade. It's free to post and you're never locked in. For urgent plumbing problems across the UK, it's the quickest way to find professional help.",
    relatedLinks: [
      { label: "Post a Job", href: "/post-job" },
      { label: "Plumbers in London", href: "/plumbers-london" },
      { label: "Plumbers in Birmingham", href: "/plumbers-birmingham" },
      { label: "Plumbers in Manchester", href: "/plumbers-manchester" },
    ],
    faqs: [
      { q: "How do I find an emergency plumber?", a: 'Post your emergency on <a href="/post-job">VeriTrade</a> with a description of the problem. Mark it as urgent and local plumbers will respond fast.' },
      { q: "How quickly can an emergency plumber respond?", a: "Many plumbers on VeriTrade respond within minutes for emergencies. It depends on your area and the time of day." },
      { q: "Is there a call-out fee?", a: "VeriTrade never charges fees. Individual plumbers may have their own call-out rates — they'll include this in their quote." },
      { q: "Can I find a plumber available right now?", a: "Yes. Post your job as urgent and plumbers who are currently available will respond first." },
    ],
  }),
  generateIntentPage({
    slug: "find-electrician-near-me",
    metaTitle: "Find an Electrician Near Me | VeriTrade UK",
    metaDescription: "Need an electrician? Post your job for free and get quotes from verified electricians in your area. No lead fees.",
    h1: "Find an Electrician Near You",
    subheadline: "Rewires, sockets, fuse boards, lighting — post your electrical job free and get quotes from verified electricians nearby.",
    trustText: "Electrical work needs to be done right. VeriTrade connects you with verified electricians who are qualified and reviewed. Post your job, get quotes, and choose the electrician you trust. No lead fees, no obligation.",
    seoContent: "Looking for a local electrician? Whether you need a full rewire, new sockets, a fuse board upgrade, or lighting installation, VeriTrade makes it easy to find qualified electricians near you.\n\nPost your job with a description of the work and your postcode. Verified electricians in your area will respond with quotes and availability. You can compare their profiles, read reviews, and choose the right one for your project.\n\nAll electricians on VeriTrade go through our verification process. There are no lead fees and posting is always free. It's the simple, transparent way to find an electrician.",
    relatedLinks: [
      { label: "Post a Job", href: "/post-job" },
      { label: "Electricians in London", href: "/electricians-london" },
      { label: "Electricians in Birmingham", href: "/electricians-birmingham" },
    ],
    faqs: [
      { q: "How do I find an electrician near me?", a: 'Post your electrical job on <a href="/post-job">VeriTrade</a> with your postcode. Verified electricians in your area will respond with quotes.' },
      { q: "Are electricians on VeriTrade qualified?", a: "Yes. All electricians on VeriTrade are verified. You can check their credentials, reviews, and ratings on their profile." },
      { q: "What electrical work can I post?", a: "Rewiring, fuse board upgrades, socket installations, lighting, EV charger fitting, safety inspections — any electrical job." },
      { q: "Is it free to find an electrician?", a: "Yes. Posting is free and there are no lead fees. You only pay the electrician you choose to hire." },
    ],
  }),
  generateIntentPage({
    slug: "hire-a-painter",
    metaTitle: "Hire a Painter | Verified Decorators | VeriTrade UK",
    metaDescription: "Find verified painters and decorators near you. Post your painting job for free and compare quotes. No lead fees.",
    h1: "Hire a Painter or Decorator",
    subheadline: "Interior, exterior, full house redecorations — post your painting job free and let verified painters quote you.",
    trustText: "Good painters are in demand, and finding one you can trust matters. VeriTrade lets you post your painting job and receive quotes from verified painters and decorators. Compare, review, and choose — with zero lead fees.",
    seoContent: "Planning a paint job? Whether it's freshening up a single room or a full house redecoration inside and out, finding the right painter makes all the difference. VeriTrade connects you with verified painters and decorators across the UK.\n\nPost your job with details of the work needed and your location. Painters in your area will send you quotes so you can compare pricing and availability. Check their profiles and reviews before you decide.\n\nThere are no lead fees on VeriTrade. Post for free, get quotes, and hire on your terms.",
    relatedLinks: [
      { label: "Post a Job", href: "/post-job" },
      { label: "Painters in London", href: "/painters-london" },
      { label: "Painters in Manchester", href: "/painters-manchester" },
    ],
    faqs: [
      { q: "How do I hire a painter?", a: 'Post your painting job on <a href="/post-job">VeriTrade</a> for free. Describe the work and local painters will send you quotes.' },
      { q: "Can I get quotes for exterior painting?", a: "Yes. You can post any painting job — interior, exterior, wallpapering, wood staining, or full redecorations." },
      { q: "How do I know if a painter is good?", a: "Every painter on VeriTrade is verified. You can also check their reviews, ratings, and portfolio of previous work." },
      { q: "Do painters charge for quotes?", a: "Most painters on VeriTrade provide free quotes. The platform itself never charges you anything." },
    ],
  }),
  generateIntentPage({
    slug: "get-plumbing-quotes",
    metaTitle: "Get Plumbing Quotes | Free & No Obligation | VeriTrade UK",
    metaDescription: "Post your plumbing job and get free quotes from verified plumbers. Bathrooms, boilers, leaks — all covered.",
    h1: "Get Plumbing Quotes",
    subheadline: "Bathroom fitting, boiler repairs, new plumbing — post your job free and get quotes from trusted plumbers near you.",
    trustText: "Getting plumbing quotes shouldn't involve chasing people or paying fees. On VeriTrade, you post your job once and verified plumbers come to you. Compare quotes, check reviews, and hire when you're ready. Completely free.",
    seoContent: "Need plumbing work done? Getting multiple quotes is the smart way to find a reliable plumber at a fair price. VeriTrade makes it easy — describe your plumbing job, set your location, and verified plumbers in your area will respond.\n\nFrom bathroom installations and boiler repairs to leak fixes and radiator replacements, our platform covers all types of plumbing work. Compare quotes side by side, read genuine reviews, and choose the plumber that's right for you.\n\nPosting on VeriTrade is always free. No lead fees, no hidden charges, no obligation.",
    relatedLinks: [
      { label: "Post a Job", href: "/post-job" },
      { label: "Plumbers in London", href: "/plumbers-london" },
      { label: "Plumbers in Birmingham", href: "/plumbers-birmingham" },
      { label: "Emergency Plumber", href: "/emergency-plumber" },
    ],
    faqs: [
      { q: "How do I get plumbing quotes?", a: 'Post your plumbing job on <a href="/post-job">VeriTrade</a> for free. Describe the work and verified plumbers will send you quotes.' },
      { q: "What plumbing work can I get quotes for?", a: "Bathroom installations, boiler repairs, leak fixes, radiator replacements, pipe work, and any other plumbing job." },
      { q: "How many plumbing quotes will I get?", a: "Typically 2 to 5 quotes, depending on your area. You can compare them all before deciding." },
      { q: "Is there any cost involved?", a: "No. Posting your plumbing job is completely free. There are no lead fees or hidden charges on VeriTrade." },
      { q: "Can I get urgent plumbing quotes?", a: 'Yes. Mark your job as urgent for faster responses. For emergencies, try our <a href="/emergency-plumber">emergency plumber</a> page.' },
    ],
  }),
];

// ============ COMPETITOR PAGES ============

const COMPETITOR_PAGES = [
  generateIntentPage({
    slug: "checkatrade-alternative",
    metaTitle: "Checkatrade Alternative — No Lead Fees | VeriTrade UK",
    metaDescription: "Looking for a Checkatrade alternative? VeriTrade offers verified trades with no lead fees. Post your job free.",
    h1: "Checkatrade Alternative — Find Trades Without Lead Fees",
    subheadline: "Tired of expensive lead fees? VeriTrade connects you with verified tradespeople for free. Post your job, get quotes, pay nothing.",
    whyPoints: [
      "No lead fees — trades quote you directly",
      "Verified tradespeople across the UK",
      "Free to post any job",
      "Compare multiple quotes easily",
      "No subscription needed to hire",
    ],
    trustText: "Many homeowners are looking for an alternative to platforms that charge tradespeople per lead — costs that often get passed on to you. VeriTrade works differently. Posting a job is free, there are no lead fees, and verified tradespeople quote you directly. You stay in control.",
    seoContent: "If you're looking for a Checkatrade alternative, you're not alone. Many UK homeowners and tradespeople are switching to platforms that don't rely on expensive lead fees. VeriTrade is built around a simple idea: connecting you directly with verified trades, without the hidden costs.\n\nOn VeriTrade, posting a job is completely free. You describe what you need, local tradespeople send you quotes, and you choose who to hire. There's no obligation and no pressure. For tradespeople, it means getting access to real jobs without paying per lead.\n\nWhether you need a builder, plumber, electrician, roofer, or any other trade, VeriTrade covers it. Verified profiles, genuine reviews, and transparent pricing — the way hiring trades should work.",
    relatedLinks: [
      { label: "Post a Job", href: "/post-job" },
      { label: "MyBuilder Alternative", href: "/mybuilder-alternative" },
      { label: "Cashback Offer", href: "/cashback-offer" },
    ],
    faqs: [
      { q: "How is VeriTrade different from Checkatrade?", a: "VeriTrade never charges tradespeople per lead. On Checkatrade, trades pay for each enquiry — a cost often passed on to you. On VeriTrade, posting is free for everyone." },
      { q: "Is VeriTrade free for homeowners?", a: 'Yes. Posting a job on <a href="/post-job">VeriTrade</a> is completely free. There are no fees, no subscriptions, and no hidden costs.' },
      { q: "Do tradespeople on VeriTrade pay for leads?", a: "No. VeriTrade does not charge lead fees. Trades respond to jobs directly, which means you get fairer quotes without inflated prices." },
      { q: "Are tradespeople on VeriTrade verified?", a: "Yes. Every tradesperson goes through a verification process. You can also check their profile, reviews, and ratings." },
      { q: "Can I compare VeriTrade quotes?", a: "Absolutely. Post your job once and receive multiple quotes. Compare pricing, profiles, and reviews before making your decision." },
    ],
  }),
  generateIntentPage({
    slug: "mybuilder-alternative",
    metaTitle: "MyBuilder Alternative — Free Job Posting | VeriTrade UK",
    metaDescription: "VeriTrade is a MyBuilder alternative with no lead fees. Post your job free, get quotes from verified trades.",
    h1: "MyBuilder Alternative — Post Jobs Free, No Lead Fees",
    subheadline: "VeriTrade gives you the same access to quality tradespeople — without charging them per lead. That means better service for you.",
    whyPoints: [
      "No lead fees for trades or customers",
      "Verified tradespeople you can trust",
      "Free job posting",
      "Direct communication with trades",
      "UK-wide coverage",
    ],
    trustText: "Platforms that charge per lead create problems — trades factor those costs into their quotes, and some won't respond to smaller jobs. VeriTrade removes that barrier. Trades connect with you directly, and nobody pays for leads.",
    seoContent: "Looking for a MyBuilder alternative? VeriTrade offers a better way to find tradespeople in the UK. No lead fees, no bidding wars, and no hidden charges. Just direct connections between homeowners and verified trades.\n\nPost your job for free, whether it's a small repair or a major project. Verified tradespeople in your area will send you quotes. You compare profiles and reviews, then choose who to hire — on your terms.\n\nFor tradespeople, VeriTrade means access to real jobs without the cost of paying per lead. For customers, it means fairer quotes and more responsive trades. Everyone wins.",
    relatedLinks: [
      { label: "Post a Job", href: "/post-job" },
      { label: "Checkatrade Alternative", href: "/checkatrade-alternative" },
      { label: "Cashback Offer", href: "/cashback-offer" },
    ],
    faqs: [
      { q: "How is VeriTrade different from MyBuilder?", a: "On MyBuilder, tradespeople pay per lead. On VeriTrade, there are no lead fees at all. That means trades can offer you fairer prices." },
      { q: "Is VeriTrade free to use?", a: 'Yes. <a href="/post-job">Post your job</a> for free. There are no charges for homeowners or tradespeople on VeriTrade.' },
      { q: "Do I get the same quality of tradespeople?", a: "Yes. VeriTrade has verified tradespeople across the UK covering all trades — builders, plumbers, electricians, roofers, and more." },
      { q: "Can I read reviews of tradespeople?", a: "Yes. Every tradesperson on VeriTrade has a profile with reviews and ratings from previous customers." },
      { q: "Why are tradespeople switching from MyBuilder?", a: "Lead fees add up quickly. VeriTrade gives tradespeople access to real jobs without per-lead charges, which means they can focus on delivering great work." },
    ],
  }),
];

// ============ VERICLEAN INTENT PAGES ============

const VERICLEAN_INTENT_PAGES = [
  generateIntentPage({
    slug: "book-a-cleaner-near-me",
    isVeriClean: true,
    metaTitle: "Book a Cleaner Near Me | VeriClean UK",
    metaDescription: "Find and book verified cleaners near you. Regular cleans, deep cleans, end of tenancy — all covered. Free to post.",
    h1: "Book a Cleaner Near You",
    subheadline: "Tell us what you need, where you are, and get matched with verified local cleaners. Quick, easy, free.",
    ctaText: "Book a Cleaner",
    howItWorks: [
      { step: "1", title: "Describe your clean", desc: "Tell us the type of clean, property size, and when you need it." },
      { step: "2", title: "Get matched", desc: "Verified cleaners in your area respond with availability." },
      { step: "3", title: "Book and relax", desc: "Choose the cleaner you like and get your space sorted." },
    ],
    whyPoints: [
      "Verified, trusted cleaners",
      "Free to post your cleaning job",
      "Regular, deep, and one-off cleans",
      "Fast responses from local cleaners",
      "No booking fees",
    ],
    trustText: "Finding a cleaner you can trust shouldn't be a gamble. VeriClean verifies every cleaner on the platform so you know who's coming into your home. Post your cleaning request, compare options, and book with confidence.",
    seoContent: "Need a cleaner near you? VeriClean makes it simple to find verified cleaning professionals in your area. Whether you need a regular weekly clean, a deep clean before guests arrive, or an end-of-tenancy clean to get your deposit back, our platform has you covered.\n\nDescribe what you need, set your postcode, and verified cleaners nearby will get in touch. You can check their profile, read reviews from other customers, and choose the cleaner that suits you.\n\nPosting on VeriClean is free. There are no booking fees and no obligation to hire. It's the easy way to find reliable cleaning help across the UK.",
    relatedLinks: [
      { label: "Book a Cleaner", href: "/vericlean/book" },
      { label: "Cleaners in London", href: "/cleaners-london" },
      { label: "Cleaners in Birmingham", href: "/cleaners-birmingham" },
      { label: "End of Tenancy Cleaning", href: "/end-of-tenancy-cleaning" },
    ],
    faqs: [
      { q: "How do I book a cleaner near me?", a: 'Post your cleaning job on <a href="/vericlean/book">VeriClean</a> for free. Describe what you need, enter your postcode, and verified cleaners nearby will send you quotes.' },
      { q: "Are cleaners on VeriClean verified?", a: "Yes. Every cleaner on VeriClean goes through a verification process. You can check their reviews and ratings before booking." },
      { q: "What types of cleaning can I book?", a: "Regular house cleans, deep cleans, end of tenancy, one-off cleans, oven cleaning, office cleaning, and more." },
      { q: "Is there a booking fee?", a: "No. VeriClean is completely free for customers. There are no booking fees, no lead charges, and no hidden costs." },
      { q: "How quickly will cleaners respond?", a: "Most cleaners respond within a few hours. For urgent requests, many can offer same-day availability." },
      { q: "Do I have to accept a quote?", a: "No. You can compare multiple quotes from verified cleaners and only book when you're ready. There's no obligation." },
    ],
  }),
  generateIntentPage({
    slug: "end-of-tenancy-cleaning",
    isVeriClean: true,
    metaTitle: "End of Tenancy Cleaning | Verified Cleaners | VeriClean UK",
    metaDescription: "Find verified end of tenancy cleaners near you. Protect your deposit with a professional deep clean. Free to post.",
    h1: "End of Tenancy Cleaning",
    subheadline: "Moving out? Get your deposit back with a professional end of tenancy clean from verified cleaners near you.",
    ctaText: "Find a Cleaner",
    trustText: "An end of tenancy clean can make the difference between getting your deposit back and losing it. VeriClean connects you with experienced, verified cleaners who specialise in move-out cleans. Post your request and get quotes quickly.",
    seoContent: "Moving out and need a thorough clean? End of tenancy cleaning is one of the most important steps in getting your deposit returned. VeriClean helps you find verified cleaners who know exactly what landlords and letting agents expect.\n\nOur cleaners handle everything — kitchens, bathrooms, floors, windows, ovens, and all the areas that get checked during inspections. Post your cleaning request with your moving date and property size, and local cleaners will respond with quotes.\n\nVeriClean is free to use. No booking fees, no obligation. Just reliable, verified cleaners ready to help you move out stress-free.",
    relatedLinks: [
      { label: "Book a Cleaner", href: "/vericlean/book" },
      { label: "Book a Cleaner Near Me", href: "/book-a-cleaner-near-me" },
      { label: "Cleaners in London", href: "/cleaners-london" },
      { label: "Cleaners in Manchester", href: "/cleaners-manchester" },
    ],
    faqs: [
      { q: "What is an end of tenancy clean?", a: "A thorough deep clean of the entire property to meet landlord and letting agent standards. It covers kitchens, bathrooms, floors, windows, ovens, and all living areas." },
      { q: "How do I book an end of tenancy cleaner?", a: 'Post your cleaning request on <a href="/vericlean/book">VeriClean</a> with your move-out date and property details. Local cleaners will respond with quotes.' },
      { q: "Will an end of tenancy clean help me get my deposit back?", a: "Yes. A professional end of tenancy clean is one of the best ways to ensure your property passes the checkout inspection and your deposit is returned." },
      { q: "How much does end of tenancy cleaning cost?", a: "Costs vary by property size and condition. Post your job on VeriClean to compare quotes from multiple verified cleaners in your area." },
      { q: "Are VeriClean cleaners experienced with move-out cleans?", a: "Yes. Many cleaners on VeriClean specialise in end of tenancy work and know exactly what landlords and agents look for." },
    ],
  }),
];

// ============ GENERATE ALL TRADE+LOCATION PAGES ============

const TRADE_LOCATION_PAGES = [];

LAUNCH_TRADES.forEach(trade => {
  LAUNCH_LOCATIONS.forEach(location => {
    const page = generateTradeLocation(trade, location);
    if (page) TRADE_LOCATION_PAGES.push(page);
  });
});

VERICLEAN_TRADES.forEach(trade => {
  VERICLEAN_LOCATIONS.forEach(location => {
    const page = generateTradeLocation(trade, location);
    if (page) TRADE_LOCATION_PAGES.push(page);
  });
});

// ============ MASTER REGISTRY ============

const ALL_PAGES = [
  ...TRADE_LOCATION_PAGES,
  ...INTENT_PAGES,
  ...COMPETITOR_PAGES,
  ...VERICLEAN_INTENT_PAGES,
];

// Map: slug -> page config
export const SEO_PAGE_MAP = {};
ALL_PAGES.forEach(p => { SEO_PAGE_MAP[p.slug] = p; });

// Array of all slugs (for routing)
export const SEO_SLUGS = ALL_PAGES.map(p => p.slug);

// Export generators for runtime use
export { generateTradeLocation, generateIntentPage, TRADE_META, LOCATIONS };

export default SEO_PAGE_MAP;
